import { Box } from '@mui/material';
import { FC } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';

import { formatDisplayDayLabel } from './displayDays';

type Props = {
  /** 期間の日（'YYYY-MM-DD'）。1 日だけなら描かない */
  days: readonly string[];
  selectedDay: string;
  onChange: (day: string) => void;
};

/**
 * 表示日タブ。期間を跨いで開くと、左右のリストがいつのデータなのか分からなかった
 * （指摘 2026/08/18）。日ごとに切り替えて 1 日ぶんだけ見せる。
 *
 * Why not MUI の Tabs: 選択中を線で示す作りで、この行だけ他のツールバーと見た目が
 * 変わる。押した状態が分かるボタンの並びに揃える（サンプル lovable と同じ形）。
 */
const DisplayDayTabs: FC<Props> = ({ days, selectedDay, onChange }) => {
  if (days.length <= 1) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        bgcolor: '#fff',
        borderBottom: '1px solid hsl(var(--border))',
      }}
    >
      <Box component="span" sx={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', flexShrink: 0 }}>
        表示日
      </Box>
      {/* 日数が多いと横に溢れるので、この行だけ横スクロールさせる */}
      <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', minWidth: 0 }}>
        {days.map((day) => {
          const selected = day === selectedDay;
          return (
            <Box
              key={day}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => {
                if (selected) return;
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.PLANNING_MAP,
                  button_name: '表示日',
                  label: day,
                });
                onChange(day);
              }}
              sx={{
                flexShrink: 0,
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
                fontSize: 12,
                fontWeight: selected ? 700 : 500,
                cursor: selected ? 'default' : 'pointer',
                border: '1px solid',
                borderColor: selected ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                color: selected ? '#fff' : 'hsl(var(--foreground))',
                bgcolor: selected ? 'hsl(var(--primary))' : 'hsl(var(--background))',
                '&:hover': selected ? undefined : { bgcolor: 'hsl(var(--secondary))' },
              }}
            >
              {formatDisplayDayLabel(day)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DisplayDayTabs;
